import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Button,
  Table,
  TableColumnsType,
  Typography,
  Grid,
  Row,
  Col,
  Progress,
  Modal,
  Space,
  Select,
  ConfigProvider,
  Spin
} from "antd";
import { FileFilled } from "@ant-design/icons";
import { GameDownloadInfo2, AntSelectOptionProps } from "../../interface";
import { downloadGameFile, getAllGameFiles } from "../../apiBridge";
import { useOrganization } from "../organization/OrganizationContext";
import OrgTitle from "../organization/orgTitleInfo";

const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

// Minimal shape of the File System Access API pieces we use; the standard
// DOM typings do not cover showSaveFilePicker yet.
type WritableFileStream = {
  write: (chunk: Uint8Array) => Promise<void>;
  seek: (position: number) => Promise<void>;
  close: () => Promise<void>;
  abort: () => Promise<void>;
};

type DownloadFileHandle = {
  name?: string;
  createWritable: (options?: { keepExistingData?: boolean }) => Promise<WritableFileStream>;
};

type SaveFilePicker = (options: { suggestedName: string }) => Promise<DownloadFileHandle>;

type DownloadSession = {
  key: string;
  size: number;
  handle: DownloadFileHandle;
  writable: WritableFileStream | null;
  downloaded: number;
  etag: string | null;
};

type PendingNetworkRetry = {
  key: string;
  size: number;
  attempt: number;
};

class RestartDownloadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RestartDownloadError";
    Object.setPrototypeOf(this, RestartDownloadError.prototype);
  }
}

class FileWriteError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "FileWriteError";
    Object.setPrototypeOf(this, FileWriteError.prototype);
  }
}

class IncompleteDownloadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "IncompleteDownloadError";
    Object.setPrototypeOf(this, IncompleteDownloadError.prototype);
  }
}

function getSaveFilePicker(): SaveFilePicker | null {
  const picker = (window as { showSaveFilePicker?: SaveFilePicker }).showSaveFilePicker;
  return typeof picker === "function" ? picker.bind(window) : null;
}

function isAbortError(error: unknown) {
  return error instanceof DOMException && error.name === "AbortError";
}

function isRetryableNetworkError(error: unknown) {
  if (error instanceof IncompleteDownloadError || error instanceof TypeError) {
    return true;
  }
  return error instanceof DOMException &&
    (error.name === "NetworkError" || error.name === "AbortError");
}

function contentRangeStartsAt(header: string | null, expectedStart: number) {
  if (!header) return false;
  const match = /^bytes (\d+)-(\d+)\/(\d+)$/i.exec(header);
  return match !== null && Number(match[1]) === expectedStart;
}

function formatBytes(value: number) {
  if (!value) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const unit = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  return `${(value / 1024 ** unit).toFixed(unit > 1 ? 1 : 0)} ${units[unit]}`;
}

function formatRemainingTime(seconds: number) {
  if (!Number.isFinite(seconds) || seconds <= 0) return null;

  const roundedSeconds = Math.max(1, Math.ceil(seconds));
  if (roundedSeconds < 60) return `${roundedSeconds}s remaining`;

  const roundedMinutes = Math.ceil(roundedSeconds / 60);
  if (roundedMinutes < 60) return `${roundedMinutes}m remaining`;

  const hours = Math.floor(roundedMinutes / 60);
  const minutes = roundedMinutes % 60;
  return minutes > 0 ? `${hours}h ${minutes}m remaining` : `${hours}h remaining`;
}

type DownloadSpeedSample = {
  timestamp: number;
  bytes: number;
};

const DOWNLOAD_SPEED_WINDOW_MS = 3000;
const DOWNLOAD_SPEED_UPDATE_INTERVAL_MS = 250;
const DOWNLOAD_RETRY_MAX_DELAY_MS = 30000;

const GameDownloadLayout: React.FC = () => {
  const [data, setData] = useState<GameDownloadInfo2[]>([]);
  const [dataLoading, setDataLoading] = useState(false);
  const screens = useBreakpoint();
  const { organization, loading } = useOrganization();
  const [filteredGameFiles, setFilteredGameFiles] = useState<GameDownloadInfo2[]>([])
  const [filterPlatformValue, setFilterPlatformValue] = useState("")
  const [filterVersionValue, setFilterVersionValue] = useState("")
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadedBytes, setDownloadedBytes] = useState(0);
  const [downloadSpeed, setDownloadSpeed] = useState(0);
  const [downloadModal, setDownloadModal] = useState(false);
  const [cancelConfirmModal, setCancelConfirmModal] = useState(false);
  const [completedDownloadName, setCompletedDownloadName] = useState<string | null>(null);
  const [pendingNetworkRetry, setPendingNetworkRetry] = useState<PendingNetworkRetry | null>(null);
  const [recoveringNetwork, setRecoveringNetwork] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [errorModal, setErrorModal] = useState(false);
  const [resumableBytes, setResumableBytes] = useState(0);
  const [currentDownloadFile, setCurrentDownloadFile] = useState<{filename: string, size: number} | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [fetchRetryCount, setFetchRetryCount] = useState(0);
  const fetchingRef = useRef<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const downloadSessionRef = useRef<DownloadSession | null>(null);
  const automaticRetryAttemptRef = useRef(0);

  const discardDownloadSession = useCallback(async () => {
    const session = downloadSessionRef.current;
    downloadSessionRef.current = null;
    setResumableBytes(0);
    if (session?.writable) {
      await session.writable.abort().catch(() => {});
    }
  }, []);

  const requestCancelDownload = useCallback(() => {
    setCancelConfirmModal(true);
  }, []);

  const confirmCancelDownload = useCallback(() => {
    setCancelConfirmModal(false);
    const controller = abortControllerRef.current;
    if (controller) {
      controller.abort();
      return;
    }

    setPendingNetworkRetry(null);
    setRecoveringNetwork(false);
    setDownloadModal(false);
    setCurrentDownloadFile(null);
    automaticRetryAttemptRef.current = 0;
    void discardDownloadSession();
  }, [discardDownloadSession]);

  useEffect(() => () => {
    abortControllerRef.current?.abort();
    const session = downloadSessionRef.current;
    downloadSessionRef.current = null;
    if (session?.writable) {
      void session.writable.abort().catch(() => {});
    }
  }, []);

  const { canUsePlatformSelect, canUseVersionSelect } = useMemo(() => {
    const getFilterOptions = (arr: string[], defaultValue: string): AntSelectOptionProps[] => {
      return [
        { value: "", label: defaultValue },
        ...arr.map(item => ({ 
          value: item, 
          label: item === 'pico' ? 'VR' : item.toUpperCase() 
        }))
      ];
    };

    if (!data || data.length === 0) {
      return {
        canUsePlatformSelect: getFilterOptions([], "All Platform"),
        canUseVersionSelect: getFilterOptions([], "All Versions")
      };
    }

    const allPlatforms = data.flatMap(item => 
      item.files.map(file => file.platform)
    );
    const platforms = allPlatforms.filter((platform, index) => 
      allPlatforms.indexOf(platform) === index
    ).sort();
    
    const allVersions = data.map(item => item.version);
    const versions = allVersions.filter((version, index) => 
      allVersions.indexOf(version) === index
    ).sort();
    
    return {
      canUsePlatformSelect: getFilterOptions(platforms, "All Platform"),
      canUseVersionSelect: getFilterOptions(versions, "All Versions")
    };
  }, [data]);

  // Fetch game files when organization is available
  useEffect(() => {
    if (!organization?.ID) return;

    const fetchGameFiles = async () => {  
      if (fetchingRef.current) {
        return;
      }
      
      console.info("game downloads call", organization?.ID);
      
      try {
        fetchingRef.current = true;
        setDataLoading(true);
        
        const response = await getAllGameFiles(organization.ID);
        if (response.status === 200) {
          const gameFiles = await response.json();
          console.info("Game Files", gameFiles)
          
          const filteredData = gameFiles.gamefiles
            .filter((item: any) => item.tags?.upload_date !== "" )
            .map((item: any) => ({
              key: item.key,
              ...item.tags,
            }));

          
          const groupedByVersion = filteredData.reduce((acc: any, item: GameDownloadInfo2) => {
            if (!acc[item.version]) {
              acc[item.version] = [];
            }
            acc[item.version].push(item);
            return acc;
          }, {});

          
          const result = Object.keys(groupedByVersion).map(version => ({
            version: version,
            upload_date: groupedByVersion[version][0].upload_date || "",
            files: groupedByVersion[version]
          }));

          
          setData(result);
        } else {
          const gameFiles = await response.json();
          throw new Error(gameFiles.error ||"network error")
        }
      } catch (error) {
        console.error('Failed to fetch game files:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to load game files. Please try again later.';
        setFetchError(errorMessage);
        setData([]);
      } finally {
        fetchingRef.current = false;
        setDataLoading(false);
      }
    };
    
    fetchGameFiles();
  }, [organization?.ID, fetchRetryCount]);

  const retryFetchingGameFiles = () => {
    setFetchError(null);
    setDataLoading(true);
    setFetchRetryCount(count => count + 1);
  };

  const getFile = useCallback(async (key: string, size: number) => {
    if (isDownloading) {
      return;
    }

    const filename = key.split("/").pop() || key;

    // The picker must be opened from the original click gesture. When it is
    // available, stream each response chunk directly to disk; otherwise use
    // the Blob fallback for browsers without the File System Access API.
    const existingSession = downloadSessionRef.current?.key === key
      ? downloadSessionRef.current
      : null;
    if (!existingSession) {
      automaticRetryAttemptRef.current = 0;
    }
    let totalSize = existingSession?.size ?? size;
    let writable: WritableFileStream | null = existingSession?.writable ?? null;
    if (existingSession && !writable) {
      try {
        writable = existingSession.downloaded > 0
          ? await existingSession.handle.createWritable({ keepExistingData: true })
          : await existingSession.handle.createWritable();
        if (existingSession.downloaded > 0) {
          await writable.seek(existingSession.downloaded);
        }
        existingSession.writable = writable;
      } catch (error) {
        if (writable) {
          await writable.abort().catch(() => {});
        }
        existingSession.writable = null;
        existingSession.downloaded = 0;
        existingSession.etag = null;
        setResumableBytes(0);
        setCurrentDownloadFile({ filename: key, size: totalSize });
        setDownloadError(error instanceof Error ? error.message : "Unable to reopen the partial download.");
        setErrorModal(true);
        return;
      }
    } else if (!existingSession && !writable) {
      const picker = getSaveFilePicker();
      if (picker) {
        try {
          const handle = await picker({ suggestedName: filename });
          writable = await handle.createWritable();
          downloadSessionRef.current = {
            key,
            size: totalSize,
            handle,
            writable,
            downloaded: 0,
            etag: null,
          };
        } catch (error) {
          if (!isAbortError(error)) {
            setDownloadError(error instanceof Error ? error.message : "Unable to open the save dialog.");
            setErrorModal(true);
          }
          return;
        }
      }
    }

    const startingOffset = downloadSessionRef.current?.key === key
      ? downloadSessionRef.current.downloaded
      : 0;
    const expectedETag = downloadSessionRef.current?.key === key
      ? downloadSessionRef.current.etag
      : null;

    const controller = new AbortController();
    let scheduledNetworkRetry = false;
    abortControllerRef.current = controller;
    setIsDownloading(true);
    setDownloadProgress(totalSize > 0 ? Math.min((startingOffset / totalSize) * 100, 100) : 0);
    setDownloadedBytes(startingOffset);
    setDownloadSpeed(0);
    setDownloadModal(true);
    setDownloadError(null);
    setResumableBytes(0);
    setCurrentDownloadFile({ filename: key, size: totalSize });

    try {
      if (totalSize > 0 && startingOffset > totalSize) {
        throw new RestartDownloadError("The partial download is larger than the current file.");
      }
      if (writable && totalSize > 0 && startingOffset === totalSize) {
        const completedName = downloadSessionRef.current?.handle.name || filename;
        try {
          await writable.close();
        } catch (error) {
          throw new FileWriteError(error instanceof Error ? error.message : "Unable to finish writing the file.");
        }
        downloadSessionRef.current = null;
        setDownloadModal(false);
        setCurrentDownloadFile(null);
        setCompletedDownloadName(completedName);
        setRecoveringNetwork(false);
        automaticRetryAttemptRef.current = 0;
        return;
      }

      const response = startingOffset > 0
        ? await downloadGameFile(key, controller.signal, startingOffset, expectedETag ?? undefined)
        : await downloadGameFile(key, controller.signal);

      if (startingOffset > 0 && response.status === 412) {
        await response.body?.cancel().catch(() => {});
        throw new RestartDownloadError("The file changed on the server. Retry will restart the download.");
      }
      if (!response.ok || !response.body) {
        throw new Error(`Download failed with status ${response.status}.`);
      }

      const responseSizeHeader = response.headers.get("X-Content-Length");
      const responseSize = responseSizeHeader === null ? Number.NaN : Number(responseSizeHeader);
      if (Number.isSafeInteger(responseSize) && responseSize >= 0) {
        if (startingOffset > 0 && totalSize > 0 && responseSize !== totalSize) {
          await response.body.cancel().catch(() => {});
          throw new RestartDownloadError("The file size changed on the server. Retry will restart the download.");
        }
        totalSize = responseSize;
        setCurrentDownloadFile({ filename: key, size: totalSize });
        const sizedSession = downloadSessionRef.current;
        if (sizedSession?.key === key) {
          sizedSession.size = totalSize;
        }
      }
      if (startingOffset > 0) {
        if (response.status !== 206) {
          await response.body.cancel().catch(() => {});
          throw new RestartDownloadError("The server did not accept the resume request. Retry will restart the download.");
        }
        if (!contentRangeStartsAt(response.headers.get("Content-Range"), startingOffset)) {
          await response.body.cancel().catch(() => {});
          throw new RestartDownloadError("The server returned an unexpected download range. Retry will restart the download.");
        }
      }

      const responseETag = response.headers.get("X-Gamefile-ETag");
      const session = downloadSessionRef.current;
      if (session?.key === key) {
        if (session.etag && responseETag && session.etag !== responseETag) {
          await response.body.cancel().catch(() => {});
          throw new RestartDownloadError("The file changed on the server. Retry will restart the download.");
        }
        if (!session.etag && responseETag) {
          session.etag = responseETag;
        }
      }

      const reader = response.body.getReader();
      const chunks: Uint8Array[] = [];
      let downloaded = startingOffset;
      let lastSpeedUpdateAt = performance.now();
      const speedSamples: DownloadSpeedSample[] = [{
        timestamp: lastSpeedUpdateAt,
        bytes: startingOffset,
      }];
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (writable) {
          try {
            await writable.write(value);
          } catch (error) {
            await reader.cancel().catch(() => {});
            throw new FileWriteError(error instanceof Error ? error.message : "Unable to write the downloaded file.");
          }
        } else {
          chunks.push(value);
        }
        downloaded += value.byteLength;
        if (recoveringNetwork) {
          setRecoveringNetwork(false);
        }
        const activeSession = downloadSessionRef.current;
        if (activeSession?.key === key && activeSession.writable === writable) {
          activeSession.downloaded = downloaded;
        }
        setDownloadedBytes(downloaded);

        const now = performance.now();
        if (now - lastSpeedUpdateAt >= DOWNLOAD_SPEED_UPDATE_INTERVAL_MS) {
          speedSamples.push({ timestamp: now, bytes: downloaded });
          while (
            speedSamples.length > 2 &&
            speedSamples[1].timestamp < now - DOWNLOAD_SPEED_WINDOW_MS
          ) {
            speedSamples.shift();
          }

          const oldestSample = speedSamples[0];
          const elapsedSeconds = (now - oldestSample.timestamp) / 1000;
          if (elapsedSeconds > 0) {
            setDownloadSpeed((downloaded - oldestSample.bytes) / elapsedSeconds);
          }
          lastSpeedUpdateAt = now;
        }

        if (totalSize > 0) {
          setDownloadProgress(Math.min((downloaded / totalSize) * 100, 100));
        }
      }
      if (totalSize > 0 && downloaded !== totalSize) {
        if (downloaded > totalSize) {
          throw new RestartDownloadError("The server returned more data than expected. Retry will restart the download.");
        }
        throw new IncompleteDownloadError(`The download ended early at ${downloaded} of ${totalSize} bytes.`);
      }

      if (writable) {
        const completedName = downloadSessionRef.current?.handle.name || filename;
        try {
          await writable.close();
        } catch (error) {
          throw new FileWriteError(error instanceof Error ? error.message : "Unable to finish writing the file.");
        }
        downloadSessionRef.current = null;
        setCompletedDownloadName(completedName);
        setRecoveringNetwork(false);
        automaticRetryAttemptRef.current = 0;
      } else {
        const blobURL = URL.createObjectURL(new Blob(chunks));
        const anchor = document.createElement("a");
        anchor.href = blobURL;
        anchor.download = filename;
        anchor.click();
        URL.revokeObjectURL(blobURL);
      }
      setCancelConfirmModal(false);
      setDownloadModal(false);
      setCurrentDownloadFile(null);
    } catch (error) {
      const userCancelled = controller.signal.aborted;
      const activeSession = downloadSessionRef.current;
      const shouldWaitForNetwork = !userCancelled &&
        activeSession?.key === key &&
        isRetryableNetworkError(error);

      if (userCancelled) {
        await discardDownloadSession();
        setPendingNetworkRetry(null);
        setRecoveringNetwork(false);
        setDownloadModal(false);
        setCurrentDownloadFile(null);
        automaticRetryAttemptRef.current = 0;
      } else if (shouldWaitForNetwork && activeSession) {
        if (activeSession.writable) {
          try {
            // Commit the verified prefix before waiting. Once connectivity
            // returns, the same handle is reopened and positioned at offset.
            await activeSession.writable.close();
          } catch {
            await activeSession.writable.abort().catch(() => {});
            activeSession.downloaded = 0;
            activeSession.etag = null;
          }
          activeSession.writable = null;
        }

        scheduledNetworkRetry = true;
        const attempt = automaticRetryAttemptRef.current + 1;
        automaticRetryAttemptRef.current = attempt;
        setRecoveringNetwork(true);
        setPendingNetworkRetry({ key, size: activeSession.size || totalSize, attempt });
        setDownloadModal(true);
        setCancelConfirmModal(false);
        setErrorModal(false);
        setDownloadedBytes(activeSession.downloaded);
        setDownloadProgress(
          activeSession.size > 0
            ? Math.min((activeSession.downloaded / activeSession.size) * 100, 100)
            : 0,
        );
        setDownloadSpeed(0);
      } else if (activeSession?.key === key) {
        if (error instanceof RestartDownloadError || error instanceof FileWriteError) {
          if (activeSession.writable) {
            await activeSession.writable.abort().catch(() => {});
          }
          // The bytes on disk cannot safely be appended to, but the file
          // handle is still valid. Retry can restart the same chosen file
          // without opening the save picker again.
          activeSession.writable = null;
          activeSession.downloaded = 0;
          activeSession.etag = null;
        } else if (activeSession.writable) {
          try {
            // Commit the verified prefix. Resume will reopen this same handle
            // with keepExistingData and seek to the recorded offset.
            await activeSession.writable.close();
          } catch {
            await activeSession.writable.abort().catch(() => {});
            activeSession.downloaded = 0;
            activeSession.etag = null;
          }
          activeSession.writable = null;
        }
        setDownloadModal(false);
        setRecoveringNetwork(false);
        setCancelConfirmModal(false);
        setResumableBytes(downloadSessionRef.current?.downloaded ?? 0);
        console.error('Download failed:', error);
        setDownloadError(error instanceof Error ? error.message : 'Download failed. Please try again.');
        setErrorModal(true);
      } else {
        setDownloadModal(false);
        setRecoveringNetwork(false);
        setCancelConfirmModal(false);
        if (!userCancelled) {
          console.error('Download failed:', error);
          setDownloadError(error instanceof Error ? error.message : 'Download failed. Please try again.');
          setErrorModal(true);
        }
      }
    } finally {
      abortControllerRef.current = null;
      setIsDownloading(false);
      if (!scheduledNetworkRetry) {
        setDownloadProgress(0);
        setDownloadedBytes(0);
        setDownloadSpeed(0);
      }
    }
  }, [discardDownloadSession, isDownloading, recoveringNetwork]);

  useEffect(() => {
    if (!pendingNetworkRetry || isDownloading) {
      return;
    }

    let disposed = false;
    const resumeDownload = () => {
      if (disposed) return;
      setPendingNetworkRetry(null);
      void getFile(pendingNetworkRetry.key, pendingNetworkRetry.size);
    };

    if (!navigator.onLine) {
      window.addEventListener("online", resumeDownload, { once: true });
      return () => {
        disposed = true;
        window.removeEventListener("online", resumeDownload);
      };
    }

    const retryDelay = Math.min(
      1000 * 2 ** Math.min(pendingNetworkRetry.attempt - 1, 5),
      DOWNLOAD_RETRY_MAX_DELAY_MS,
    );
    const retryTimer = window.setTimeout(resumeDownload, retryDelay);
    return () => {
      disposed = true;
      window.clearTimeout(retryTimer);
    };
  }, [getFile, isDownloading, pendingNetworkRetry]);

  const handleRetryDownload = useCallback(() => {
    if (currentDownloadFile) {
      setErrorModal(false);
      getFile(currentDownloadFile.filename, currentDownloadFile.size);
    }
  }, [currentDownloadFile, getFile]);

  const handleCloseError = useCallback(() => {
    void discardDownloadSession();
    setErrorModal(false);
    setDownloadError(null);
    setCurrentDownloadFile(null);
  }, [discardDownloadSession]);

  useEffect(() => {
    if (data.length > 0) {
      let newValues :GameDownloadInfo2[] = [];

      for (let i = 0; i < data.length; i++) {
        let files = [];

        if (filterVersionValue !== "") {
          if (data[i].version !== filterVersionValue) continue;
        }
        
        if (filterPlatformValue !== "") {
          for (let k = 0; k < data[i].files.length; k++) {
            if (data[i].files[k].platform !== filterPlatformValue) continue;
            files.push(data[i].files[k]);
          }
        } else {
          files = data[i].files;
        }

        if (files.length > 0) {
          newValues.push({
            ...data[i],
            files: files
          });
        }
      }

      newValues.sort((a, b) => {
        if (!a.upload_date || !b.upload_date) return 0;
        return a.upload_date > b.upload_date ? -1 : 1;
      });

      setFilteredGameFiles(newValues);
    }
  }, [data, filterPlatformValue, filterVersionValue]);

  if (loading) {
    return (
      <div
        style={{
          background: "white",
          borderRadius: 4,
          boxShadow: "0 0 8px 0 #cbcbcb",
          padding: 20,
          textAlign: "center"
        }}
      >
        <Title level={4}>Loading organization...</Title>
      </div>
    );
  }

  if (dataLoading) {
    return (
      <div
        style={{
          background: "white",
          borderRadius: 4,
          boxShadow: "0 0 8px 0 #cbcbcb",
          padding: 20,
          textAlign: "center"
        }}
      >
        <Title level={4}>Loading...</Title>
        <Spin size="large" style={{ marginTop: 16 }} />
      </div>
    );
  }

  if (fetchError) {
    return (
      <div
        style={{
          background: "white",
          borderRadius: 4,
          boxShadow: "0 0 8px 0 #cbcbcb",
          padding: 20,
          textAlign: "center"
        }}
      >
        <Title level={4} >Error Loading Game Files</Title>
        <div style={{ 
          marginTop: 16,
          marginBottom: 16
        }}>
          <Text style={{fontSize: 18}}>{fetchError}</Text>
        </div>
        <Button type="primary" onClick={retryFetchingGameFiles}>
          Try Again
        </Button>
      </div>
    );
  }

  

  const columns: TableColumnsType<GameDownloadInfo2> = [
    {
      title: "",
      key: "version",
      dataIndex: "version",
      render: (text, row) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <FileFilled style={{ fontSize: 44, marginRight: 8 }} />
          <div>
            <Text style={{ lineHeight: "32px" }}>{text}</Text>
            {!screens.md && (
              <div>
                <Text type="secondary">{row.upload_date}</Text>
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "",
      key: "upload_date",
      dataIndex: "upload_date",
      width: 120,
      render: (text) => (
        <div style={{ textAlign: "right" }}>
          <Text style={{ fontFamily: "Monaco" }} type="secondary">{text}</Text>
        </div>
      ),
      responsive: ["md"],
    },
    {
      title: "",
      key: "downloads",
      dataIndex: "files",
      render: (arr: Array<{key: string, platform: string, size: number}>) => (
        <Row>
          {arr.map((link) => (
            <Col
              key={link.key}
              md={12}
              sm={24}
              xs={24}
              style={{ textAlign: "right" }}
            >
              <Button 
                type="link" 
                disabled={isDownloading}
                onClick={() => getFile(link.key, link.size)}
              >
                {link.platform === "pc" ? "PC" : "VR"} Download
              </Button>
            </Col>
          ))}
        </Row>
      ),
      width: screens.md ? 280 : 150,
    },
  ];

  return (
    <>
      <OrgTitle title={organization?.Name} dayFromToday={null} />
      <Modal
        footer={[
          <Button key="cancel" onClick={requestCancelDownload}>Cancel Download</Button>,
        ]}
        maskClosable={false}
        closeIcon={false}
        centered
        open={downloadModal}
        width={380}
      >
        <Title level={5}>
          {recoveringNetwork ? "Waiting for network..." : "Downloading in progress..."}
        </Title>
        <Space direction="vertical" size={0}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Do not close the browser until download is complete.
          </Text>
          {recoveringNetwork && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              The download is paused and will resume automatically when the connection is available.
            </Text>
          )}
        </Space>
        <div style={{ marginTop: 10 }}>
          {currentDownloadFile && currentDownloadFile.size > 0 ? (
            <ConfigProvider theme={{ token: { motion: false } }}>
              <Space direction="vertical" size={2} style={{ width: "100%" }}>
                <Progress
                  percent={Math.floor(downloadProgress)}
                  status={recoveringNetwork ? "normal" : "active"}
                />
                <Text type="secondary">
                  {formatBytes(downloadedBytes)} / {formatBytes(currentDownloadFile.size)}
                  {downloadSpeed > 0 && ` · ${formatBytes(downloadSpeed)}/s`}
                </Text>
                {downloadSpeed > 0 && downloadedBytes < currentDownloadFile.size && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {formatRemainingTime((currentDownloadFile.size - downloadedBytes) / downloadSpeed)}
                  </Text>
                )}
              </Space>
            </ConfigProvider>
          ) : (
            <Space>
              <Spin size="small" />
              <Text type="secondary">
                {formatBytes(downloadedBytes)} downloaded
                {downloadSpeed > 0 && ` · ${formatBytes(downloadSpeed)}/s`}
              </Text>
            </Space>
          )}
        </div>
      </Modal>

      <Modal
        title="Cancel download?"
        open={cancelConfirmModal}
        onCancel={() => setCancelConfirmModal(false)}
        maskClosable={false}
        centered
        width={400}
        footer={(
          <Space
            wrap
            size={[8, 8]}
            style={{ width: "100%", justifyContent: "flex-end" }}
          >
            <Button onClick={() => setCancelConfirmModal(false)}>
              Keep Downloading
            </Button>
            <Button danger type="primary" onClick={confirmCancelDownload}>
              Yes, Cancel Download
            </Button>
          </Space>
        )}
      >
        <Text>
          Are you sure you want to cancel? The partially downloaded file will be removed and cannot be resumed.
        </Text>
      </Modal>

      <Modal
        title="Download Complete"
        open={completedDownloadName !== null}
        onCancel={() => setCompletedDownloadName(null)}
        maskClosable={false}
        centered
        width={420}
        footer={[
          <Button key="ok" type="primary" onClick={() => setCompletedDownloadName(null)}>
            OK
          </Button>,
        ]}
      >
        <Text>
          The file was saved to your selected folder as <Text strong>{completedDownloadName}</Text>.
        </Text>
      </Modal>
      
      <Modal
        title="Download Failed"
        open={errorModal}
        onCancel={handleCloseError}
        maskClosable={false}
        centered
        width={400}
        footer={[
          <Button key="cancel" onClick={handleCloseError}>
            Cancel
          </Button>,
          <Button key="retry" type="primary" onClick={handleRetryDownload}>
            {resumableBytes > 0 ? "Resume Download" : "Retry Download"}
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            {resumableBytes > 0
              ? `${formatBytes(resumableBytes)} has already been downloaded. Retry will continue from there.`
              : "The download failed due to a network error or server issue."}
          </Text>
        </div>
        {downloadError && (
          <div style={{ 
            padding: 12, 
            backgroundColor: "#fff2f0", 
            border: "1px solid #ffccc7", 
            borderRadius: 4,
            marginBottom: 16
          }}>
            <Text type="danger" style={{ fontSize: 12 }}>
              Error: {downloadError}
            </Text>
          </div>
        )}
        <Text>
          Would you like to try downloading again?
        </Text>
      </Modal>
      <Row  style={{ backgroundColor: "#fff", padding: 20, borderBottom: "1px solid #f0f0f0" }}>
        <Col flex="100px">
          <Title level={5} style={{ display: "inline" }}>
            Files
          </Title>
        </Col>

        {!screens.xs && (
          <Col
            flex="auto"
            span={screens.xs ? undefined : 24}
            style={{ display: "flex", justifyContent: "end" }}
          >
            <Space size="middle">
              <Select 
                style={{ width: 120 }} 
                value={filterPlatformValue} 
                variant="borderless" 
                onChange={setFilterPlatformValue}
                options={canUsePlatformSelect}
              />

              <Select 
                style={{ width: 120 }} 
                value={filterVersionValue} 
                variant="borderless" 
                onChange={setFilterVersionValue}
                options={canUseVersionSelect}
              />
            </Space>
          </Col>
        )}
        </Row>
        
          <Table
            showHeader={false}
            columns={columns}
            dataSource={filteredGameFiles}
            rowKey="version"
            scroll={{
              x: "max-content",
            }}
            style={{ backgroundColor: "#fff" }}
          />
      

      
    </>
  );
};

export default GameDownloadLayout;
